import { useState, useEffect } from 'react';
import { importMonitorService, ImportBatch, ImportLog, ImportStats } from '../../services/importMonitorService';

const statusColors: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-800',
  UPDATED: 'bg-blue-100 text-blue-800',
  FAILED: 'bg-red-100 text-red-800',
  SKIPPED: 'bg-gray-100 text-gray-800',
  COMPLETED: 'bg-green-100 text-green-800',
  RUNNING: 'bg-yellow-100 text-yellow-800',
};

export default function ImportMonitoringPage() {
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [stats, setStats] = useState<ImportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('ALL');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      importMonitorService.getBatchLogs(selectedBatch).then(setLogs);
    }
  }, [selectedBatch]);

  const loadData = async () => {
    setLoading(true);
    const [b, s] = await Promise.all([
      importMonitorService.getBatches(),
      importMonitorService.getStats(),
    ]);
    setBatches(b);
    setStats(s);
    if (b.length > 0) setSelectedBatch(b[0].id);
    setLoading(false);
  };

  const filteredLogs = filter === 'ALL' ? logs : logs.filter(l => l.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Monitoring Import Hôtels</h1>
        <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
          Actualiser
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Hôtels importés" value={stats.total_imported} color="green" />
          <StatCard label="Mis à jour" value={stats.total_updated} color="blue" />
          <StatCard label="Échoués" value={stats.total_failed} color="red" />
          <StatCard label="Complétude données" value={`${stats.data_completeness_percent.toFixed(1)}%`} color="purple" />
          <StatCard label="Chambres estimées" value={stats.hotels_with_estimated_rooms} color="yellow" />
          <StatCard label="Sans email" value={stats.hotels_without_email} color="orange" />
          <StatCard label="Batches" value={stats.total_batches} color="gray" />
          <StatCard label="Total importés + MàJ" value={stats.total_imported + stats.total_updated} color="indigo" />
        </div>
      )}

      {/* Batch selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Batches d'import</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Fichier source</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-center">Total</th>
                <th className="px-3 py-2 text-center">Importés</th>
                <th className="px-3 py-2 text-center">MàJ</th>
                <th className="px-3 py-2 text-center">Échoués</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id} className={`border-t ${selectedBatch === b.id ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs">{b.source_file}</td>
                  <td className="px-3 py-2">{new Date(b.started_at).toLocaleString('fr-FR')}</td>
                  <td className="px-3 py-2 text-center">{b.total_rows}</td>
                  <td className="px-3 py-2 text-center text-green-600 font-medium">{b.imported}</td>
                  <td className="px-3 py-2 text-center text-blue-600 font-medium">{b.updated}</td>
                  <td className="px-3 py-2 text-center text-red-600 font-medium">{b.failed}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[b.status] || 'bg-gray-100'}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => setSelectedBatch(b.id)}
                      className="text-blue-600 hover:underline text-xs">Détails</button>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">Aucun import effectué</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Logs detail */}
      {selectedBatch && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Détail du batch</h2>
            <div className="flex gap-2">
              {['ALL', 'SUCCESS', 'UPDATED', 'FAILED', 'SKIPPED'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded text-xs font-medium ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {f === 'ALL' ? 'Tous' : f}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Ligne</th>
                  <th className="px-3 py-2 text-left">Hôtel</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                  <th className="px-3 py-2 text-left">Étape</th>
                  <th className="px-3 py-2 text-left">Erreur / Warnings</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(l => (
                  <tr key={l.id} className="border-t">
                    <td className="px-3 py-2 text-gray-500">{l.row_number}</td>
                    <td className="px-3 py-2 font-medium">{l.hotel_name}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[l.status] || 'bg-gray-100'}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500">{l.step}</td>
                    <td className="px-3 py-2 text-xs">
                      {l.error_message && <span className="text-red-600">{l.error_message}</span>}
                      {l.warnings?.length > 0 && (
                        <ul className="text-yellow-600 list-disc list-inside">
                          {l.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredLogs.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">Aucun log pour ce filtre</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  const bgMap: Record<string, string> = {
    green: 'bg-green-50 border-green-200', blue: 'bg-blue-50 border-blue-200',
    red: 'bg-red-50 border-red-200', purple: 'bg-purple-50 border-purple-200',
    yellow: 'bg-yellow-50 border-yellow-200', orange: 'bg-orange-50 border-orange-200',
    gray: 'bg-gray-50 border-gray-200', indigo: 'bg-indigo-50 border-indigo-200',
  };
  const textMap: Record<string, string> = {
    green: 'text-green-700', blue: 'text-blue-700', red: 'text-red-700',
    purple: 'text-purple-700', yellow: 'text-yellow-700', orange: 'text-orange-700',
    gray: 'text-gray-700', indigo: 'text-indigo-700',
  };
  return (
    <div className={`rounded-lg border p-4 ${bgMap[color] || bgMap.gray}`}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${textMap[color] || textMap.gray}`}>{value}</p>
    </div>
  );
}
